import { defer, type Observable, of } from 'rxjs';
import type { I18nLocale } from '@ardoq/i18n';
import { action$, dispatchAction, ofType } from '@ardoq/rxbeach';
import { toggleExploreMode } from 'presentation/actions';
import { selectSlide } from 'presentation/slideNavigator/actions';
import { buildAndSaveDeck } from './buildDeck';
import { captureSlide$ } from './captureSlide';
import {
  pptxExportCancelRequested,
  pptxExportDeckBuildStarted,
  pptxExportEnded,
  pptxExportSlideStarted,
} from './pptxExport$';
import type { RunContext } from './runContext';
import { captureSlides$, type DeckRow, type SlideProgress } from './slidePlan';
import { runDeckExport$ } from './subscriptionScopes';

export type PptxExportResult = 'empty' | 'aborted' | 'saved';

const restoreViewer = ({
  slides,
  startingSlideId,
  wasExploreMode,
}: RunContext): void => {
  const slideId = startingSlideId || slides[0]?.id;
  if (slideId) {
    dispatchAction(selectSlide(slideId));
  }
  if (wasExploreMode) {
    dispatchAction(toggleExploreMode({ isExploreMode: true }));
  }
};

const reportSlideStarted = (progress: SlideProgress): void => {
  dispatchAction(pptxExportSlideStarted(progress));
};

type DeckBuild = {
  context: RunContext;
  i18nLocale: I18nLocale;
};

const savedDeck$ = (
  rows: DeckRow[],
  { context, i18nLocale }: DeckBuild
): Observable<void> =>
  defer(() => {
    dispatchAction(pptxExportDeckBuildStarted());
    return buildAndSaveDeck({
      rows,
      presentationName: context.presentationName,
      i18nLocale,
    });
  });

export const exportPresentationToPptx$ = (
  build: DeckBuild
): Observable<PptxExportResult> => {
  const { context, i18nLocale } = build;
  if (context.slides.length === 0) {
    return of('empty');
  }
  return defer(() => {
    if (context.wasExploreMode) {
      dispatchAction(toggleExploreMode({ isExploreMode: false }));
    }
    return runDeckExport$({
      rows$: captureSlides$({
        slides: context.slides,
        captureSlide$: slide => captureSlide$({ slide, i18nLocale }),
        onSlideStarted: reportSlideStarted,
      }),
      cancel$: action$.pipe(ofType(pptxExportCancelRequested)),
      saveDeck$: rows => savedDeck$(rows, build),
      teardown: () => {
        restoreViewer(context);
        dispatchAction(pptxExportEnded());
      },
    });
  });
};
